/**
 * JWT Decoder Service
 *
 * Utilities for decoding JWT tokens and extracting claims
 * without external dependencies. Does not validate signatures.
 *
 * @packageDocumentation
 */

import { Injectable } from '@angular/core';
import { TokenClaims } from '../models/api.model';

/**
 * Service for decoding JWT tokens and extracting claims.
 *
 * Handles JWT payload extraction and decoding from tokens.
 * Does NOT validate signatures - only decodes the payload.
 *
 * @example
 * ```typescript
 * const jwtDecoder = inject(JwtDecoderService);
 *
 * const claims = jwtDecoder.decode(token);
 * const orgId = jwtDecoder.extractOrganisationId(token);
 * ```
 */
@Injectable({ providedIn: 'root' })
export class JwtDecoderService {
  /**
   * Decodes a JWT token and returns the payload claims.
   *
   * @param token - JWT token string
   * @returns Decoded claims object, or empty object if decode fails
   *
   * @example
   * ```typescript
   * const claims = jwtDecoder.decode('eyJhbGc...');
   * console.log(claims.sub); // User ID
   * ```
   */
  decode(token: string): TokenClaims {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return {};
      }

      const payload = parts[1];
      const decoded = atob(payload);
      return JSON.parse(decoded);
    } catch {
      return {};
    }
  }

  /**
   * Extracts the organisation ID from a JWT token.
   *
   * Checks the `custom_namespace:org_id` claim as the primary location.
   * Falls back to URL-like custom namespace claims containing 'org'.
   *
   * @param token - JWT token string
   * @returns Organisation ID if found, null otherwise
   *
   * @example
   * ```typescript
   * const orgId = jwtDecoder.extractOrganisationId(token);
   * if (orgId) {
   *   console.log('Organisation:', orgId); // e.g., "org_demo"
   * }
   * ```
   */
  extractOrganisationId(token: string): string | null {
    const claims = this.decode(token);
    const orgIdKey = Object.keys(claims).find(
      key => key.endsWith('org_id')
    );
    
    if (orgIdKey && typeof claims[orgIdKey] === 'string') {
      return claims[orgIdKey] as string;
    }

    return null;
  }

  /**
   * Extracts the organisation display name from a JWT token.
   *
   * Checks for any claim whose key ends with `org_name` (covers the standard
   * Auth0 `org_name` claim as well as namespaced variants).
   *
   * @param token - JWT token string
   * @returns Organisation name if found, null otherwise
   *
   * @example
   * ```typescript
   * const orgName = jwtDecoder.extractOrganisationName(token);
   * if (orgName) {
   *   console.log('Organisation:', orgName); // e.g., "Acme Corp"
   * }
   * ```
   */
  extractOrganisationName(token: string): string | null {
    const claims = this.decode(token);
    const orgNameKey = Object.keys(claims).find(
      key => key.endsWith('org_name')
    );

    if (orgNameKey && typeof claims[orgNameKey] === 'string') {
      return claims[orgNameKey] as string;
    }

    return null;
  }

  /**
   * Gets the token expiration timestamp in milliseconds.
   *
   * @param token - JWT token string
   * @returns Expiration timestamp in milliseconds, or null if not found
   *
   * @example
   * ```typescript
   * const expiresAt = jwtDecoder.getExpirationMs(token);
   * if (expiresAt && Date.now() < expiresAt) {
   *   console.log('Token is still valid');
   * }
   * ```
   */
  getExpirationMs(token: string): number | null {
    const claims = this.decode(token);

    if (!claims.exp || typeof claims.exp !== 'number') {
      return null;
    }

    // JWT exp is in seconds, convert to milliseconds
    return claims.exp * 1000;
  }

  /**
   * Checks if a bearer token is expired.
   *
   * @param token - JWT token string
   * @param bufferMs - Safety buffer in milliseconds (default: 0). Use positive values
   *                   to consider tokens expired before their actual expiration time.
   *                   For example, 60000 (60 seconds) treats tokens as expired 60 seconds
   *                   before their actual expiration.
   * @returns true if token is expired or missing expiration, false if valid
   *
   * @example
   * ```typescript
   * // Check if token is expired
   * if (jwtDecoder.isTokenExpired(token)) {
   *   console.log('Token has expired, refresh needed');
   * }
   *
   * // Check with 60-second safety buffer
   * if (jwtDecoder.isTokenExpired(token, 60000)) {
   *   console.log('Token will expire soon, proactive refresh recommended');
   * }
   * ```
   */
  isTokenExpired(token: string, bufferMs = 0): boolean {
    const expirationMs = this.getExpirationMs(token);

    // If expiration not found, consider token expired
    if (expirationMs === null) {
      return true;
    }

    // Token is expired if: now >= (expiration - buffer)
    return Date.now() >= (expirationMs - bufferMs);
  }
}
